import { useCallback, useMemo, useRef, useState } from 'react';
import { useAiSidebarContext } from '@/creditflowresearch/application/AiSidebar/AiSidebarContext';
import { useIsGuest } from '@/creditflowresearch/application/User/access/hooks/useIsGuest';
import { IAskAiChatMessage } from '@/creditflowresearch/domain/vo/AskAiChat';
import { removeQuestionAndAnswers } from '../helpers/chatMessages';
import useAskAiChatAnswer from './useAskAiChatAnswer';
import useAskAiChatMessageLoader from './useAskAiChatMessageLoader';
import useAskAiChatHistory from './useAskAiChatHistory';
import useAskAiChatExport from './useAskAiChatExport';
import useAskAiChatPromptVersion from './useAskAiChatPromptVersion';

const useAskAiChat = () => {
  const { isOpen } = useAiSidebarContext();
  const isGuest = useIsGuest();
  const [isEnabled, setIsEnabled] = useState(isOpen);

  if (isOpen && !isEnabled) {
    setIsEnabled(true);
  }

  const [messages, setMessages] = useState<IAskAiChatMessage[]>([]);
  const [chatId, setChatIdState] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const chatIdRef = useRef<string | null>(null);

  const setChatId = useCallback((id: string | null): void => {
    chatIdRef.current = id;
    setChatIdState(id);
  }, []);

  const {
    chats,
    isFetching: isChatListFetching,
    hasMore: hasMoreChats,
    loadMore: loadMoreChats,
    refresh: refreshChats,
    remove: removeChat,
  } = useAskAiChatHistory({ isEnabled: isEnabled && !isGuest });

  const onChatCreated = useCallback((): void => {
    if (!isGuest) refreshChats();
  }, [isGuest, refreshChats]);

  const {
    isInitializing,
    isFetchingMessages,
    hasMoreMessages,
    loadChat,
    reloadChat,
    loadOlderMessages,
    reset: resetLoader,
  } = useAskAiChatMessageLoader({ chatIdRef, setMessages, setConnectionError });

  const {
    versions: promptVersions,
    selectedId: selectedPromptVersionId,
    select: selectPromptVersion,
    refresh: refreshPromptVersions,
    promptVersionIdRef,
  } = useAskAiChatPromptVersion({ isEnabled: isOpen && !isGuest });

  const {
    isGenerating,
    send,
    cancel,
    switchChat,
  } = useAskAiChatAnswer({
    isEnabled,
    chatIdRef,
    promptVersionIdRef,
    setChatId,
    setMessages,
    setConnectionError,
    onChatCreated,
    onBackgroundAnswerDone: reloadChat,
  });

  const { exportData, isExporting } = useAskAiChatExport({ chatId });

  const retry = useCallback((messageId: string): void => {
    if (isGenerating) return;

    const removal = removeQuestionAndAnswers(messages, messageId);
    if (!removal) return;

    setMessages(removal.messages);
    send(removal.question);
  }, [isGenerating, messages, send]);

  const canSwitchChat = !isGenerating || !!chatId;

  const startNewChat = useCallback((): void => {
    if (!canSwitchChat) return;

    switchChat(null);
    resetLoader();
    setChatId(null);
    setMessages([]);
  }, [canSwitchChat, switchChat, resetLoader, setChatId]);

  const openChat = useCallback(async (id: string): Promise<void> => {
    if (!canSwitchChat) return;

    switchChat(id);
    resetLoader();
    setChatId(id);

    await loadChat(id);
  }, [canSwitchChat, switchChat, resetLoader, setChatId, loadChat]);

  const deleteChat = useCallback(async (id: string): Promise<void> => {
    const isDeleted = await removeChat(id);

    if (isDeleted && id === chatIdRef.current) {
      startNewChat();
    }
  }, [removeChat, startNewChat]);

  const chat = useMemo(() => ({
    messages,
    connectionError,
    canSwitchChat,
    startNewChat,
    retry,
    isGenerating,
    send,
    cancel,
    isInitializing,
    isFetchingMessages,
    hasMoreMessages,
    loadOlderMessages,
    exportData,
    isExporting,
    promptVersions,
    selectedPromptVersionId,
    selectPromptVersion,
    refreshPromptVersions,
  }), [
    messages, connectionError, canSwitchChat, startNewChat, retry,
    isGenerating, send, cancel,
    isInitializing, isFetchingMessages, hasMoreMessages, loadOlderMessages,
    exportData, isExporting,
    promptVersions, selectedPromptVersionId, selectPromptVersion, refreshPromptVersions,
  ]);

  const chatList = useMemo(() => ({
    chats,
    chatId,
    canSwitchChat,
    isChatListFetching,
    hasMoreChats,
    loadMoreChats,
    openChat,
    deleteChat,
  }), [chats, chatId, canSwitchChat, isChatListFetching, hasMoreChats, loadMoreChats, openChat, deleteChat]);

  return { chat, chatList };
};

export default useAskAiChat;
